import { Ext } from "../../Doc";
import type { GuideProps } from "../Guide";

/**
 * The member guide, Brazilian Portuguese translation of en.tsx. Every number comes from `limits`;
 * keep this text in step with en.tsx.
 */
export function GuidePt({ limits: l, link }: GuideProps) {
  return (
    <>
      <h1>Como o Vrelis funciona</h1>
      <p className="lead">
        O Vrelis é um serviço para enviar mensagens criptografadas, cápsulas do tempo, que só podem ser lidas a partir de um momento que você escolhe: por
        todos no piloto ou por uma pessoa. Antes desse momento, ninguém pode ler uma cápsula: nem a pessoa para quem você escreveu, nem quem opera o Vrelis,
        nem você.
      </p>
      <p className="warn">
        Este é um piloto fechado. Baixe as cápsulas que você quer guardar (veja abaixo) e guarde sua frase de recuperação.
      </p>

      <h2>Por que dá para confiar no bloqueio</h2>
      <p>
        O bloqueio não é uma promessa do Vrelis. Ele é criptografia publicada mais um sinal temporal público que o Vrelis não controla. Veja o que o sustenta
        e no que você precisaria acreditar para que ele falhasse.
      </p>

      <h3>drand: um relógio público que assina o tempo</h3>
      <ul>
        <li>
          O <Ext href="https://drand.love"><b>drand</b></Ext> é uma rede operada pela{" "}
          <Ext href="https://www.cloudflare.com/leagueofentropy/">League of Entropy</Ext>, um grupo de organizações independentes (entre elas Cloudflare,
          Protocol Labs, Kudelski Security, EPFL e University College London) que juntas publicam um valor aleatório e assinado a cada 3 segundos. Os
          valores são numerados: rodada 1, rodada 2 e assim por diante, cada uma para um momento conhecido. Qualquer pessoa pode lê-los em vários relays
          públicos; ninguém pode ler um futuro, porque ele ainda não existe.
        </li>
        <li>
          Cada rodada é assinada com criptografia de limiar (<Ext href="https://en.wikipedia.org/wiki/BLS_digital_signature">assinaturas BLS</Ext>): cada
          organização guarda uma parte de uma chave secreta, e uma assinatura exige a maioria das partes. Nenhuma organização sozinha, nem uma minoria
          delas, consegue produzir a assinatura de uma rodada antes da hora. A rede funciona desde 2020; a cadeia que usamos,{" "}
          <Ext href="https://api.drand.sh/v2/chains/52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971/info">quicknet</Ext>, desde 2023.
          A chave pública dela está fixada no nosso código, e todo sinal que recebemos é verificado com ela.
        </li>
        <li>
          A <b>criptografia com bloqueio temporal</b> (<Ext href="https://eprint.iacr.org/2023/189">tlock</Ext>, publicada por pesquisadores da Protocol
          Labs e da Cloudflare em 2023) transforma esse relógio em um bloqueio: uma mensagem é criptografada para um número de rodada, e a assinatura da
          rodada, depois de publicada, é a única chave que a abre. Até o drand publicá-la, essa chave não existe em lugar nenhum do mundo.
        </li>
      </ul>

      <h3>Como o Vrelis o usa</h3>
      <ul>
        <li>
          Seu navegador criptografa o texto e as chaves dos seus arquivos para a rodada do drand do horário que você escolheu; Escrever mostra o número da
          rodada antes do envio. Uma cápsula privada é criptografada uma segunda vez para a chave do destinatário, então, depois do horário, só ele pode
          abri-la.
        </li>
        <li>
          Nosso servidor recebe só o resultado selado e o armazena. Ele não tem nenhuma chave que abra uma cápsula: nem antes do horário, nem depois dele, no
          caso de uma cápsula privada. Um vazamento do banco de dados exporia texto cifrado e metadados, nunca uma mensagem.
        </li>
        <li>
          Quando chega a hora, seu navegador (ou o abridor offline) busca a assinatura da rodada em um de quatro relays públicos do drand, verifica-a com a
          chave pública fixada e descriptografa. O Vrelis não participa dessa etapa.
        </li>
        <li>
          Toda cápsula é assinada pela chave do autor (<Ext href="https://en.wikipedia.org/wiki/Secp256k1">secp256k1</Ext>, o mesmo tipo de chave que o
          Bitcoin e o <Ext href="https://nostr.com">Nostr</Ext> usam), então uma cápsula alterada é detectada. A chave é criada no seu navegador a partir
          das suas 12 palavras (
          <Ext href="https://github.com/bitcoin/bips/blob/master/bip-0039.mediawiki">BIP-39</Ext>) e nunca sai dele sem criptografia.
        </li>
      </ul>

      <h3>Se o Vrelis desaparecer</h3>
      <ul>
        <li>
          Os formatos são abertos: uma cápsula é um <Ext href="https://github.com/nostr-protocol/nips/blob/master/01.md">evento Nostr</Ext> (um{" "}
          <Ext href="https://github.com/nostr-protocol/nips/blob/master/59.md">gift wrap NIP-59</Ext>, no caso de uma privada) cujo conteúdo é um arquivo{" "}
          <Ext href="https://age-encryption.org">age</Ext> com uma estrofe <Ext href="https://github.com/drand/tlock-js">tlock</Ext>. Qualquer software
          que implemente essas especificações abertas pode abrir suas cápsulas; o nosso não é necessário.
        </li>
        <li>
          <b>Baixar</b> guarda as cápsulas no seu próprio disco, junto com o abridor offline: um arquivo HTML que as abre no seu computador, sem nenhum
          servidor do Vrelis, usando o sinal do drand necessário e, no caso de uma cápsula privada, sua chave. Uma exportação completa pode ser aberta sem
          vrelis.com se o sinal necessário estiver disponível e você mantiver os arquivos e as chaves de que ela precisa.
        </li>
        <li>
          Se o próprio drand parasse antes de produzir o sinal futuro necessário, essas cápsulas poderiam ficar bloqueadas para sempre. Baixá-las com
          antecedência não elimina esse risco. Uma cápsula cuja data de abertura já passou ainda precisa do seu sinal exato e verificado: sem esse sinal no
          seu arquivo compactado ou disponível em um relay, a data sozinha não permite abri-la offline. Guarde exportações completas e as chaves de que elas
          precisam.
        </li>
      </ul>

      <h3>No que você precisaria acreditar para que ele falhasse</h3>
      <ul>
        <li>
          Que a maioria das organizações do drand se conluie para assinar uma rodada antes da hora. Elas são independentes, estão em vários países, e a
          cadeia é pública, então uma assinatura assim ficaria visível para todos. Esta é a premissa de confiança de todo o sistema.
        </li>
        <li>
          Que a página web minta. A página é código que o Vrelis entrega ao seu navegador; quem controla esse código poderia ler o texto antes de ele ser
          selado, ou sua chave depois que você a desbloqueia. É por isso que as cápsulas podem ser baixadas e abertas offline, que as impressões digitais das
          chaves são mostradas para você comparar fora do Vrelis, e que um aplicativo de celular que mantém a chave fora da página web está planejado para
          depois do piloto.
        </li>
        <li>
          Que a criptografia de hoje seja quebrada. A criptografia de curvas elípticas, que tanto o drand quanto sua chave usam, pode ser quebrada por futuros{" "}
          <Ext href="https://en.wikipedia.org/wiki/Post-quantum_cryptography">computadores quânticos</Ext>.
          Para cápsulas que devem ficar seladas por décadas, este é um risco real que o piloto não resolve.
        </li>
      </ul>

      <h2>Como entrar</h2>
      <ol>
        <li>
          Se o administrador aprovou seu endereço, cadastre-se diretamente com ele; caso contrário, abra o link do seu convite e use o endereço dele. Entre
          com essa conta Google (o Google informa ao Vrelis apenas seu nome e e-mail) ou com uma senha (enviamos um link por e-mail para confirmar o
          endereço). Depois você pode adicionar uma chave de acesso para entrar.
        </li>
        <li>
          O Vrelis oferece criar sua <b>chave</b> no navegador: sua assinatura principal, que assina o que você envia e abre as cápsulas privadas enviadas a
          você. Você pode pular essa etapa e criá-la depois em Conta, Chaves; sem uma chave, você só lê cápsulas públicas. Antes de aparecer sua <b>frase de recuperação</b> (12 palavras que reconstroem a chave em qualquer dispositivo), pedimos que você garanta que ninguém possa ver ou
          gravar sua tela.
        </li>
        <li>Escreva as 12 palavras no papel. Você digita três delas de volta e depois entra novamente para confirmar que é você: com sua senha ou uma chave de acesso, ou com o Google para sua primeiríssima chave.</li>
        <li>
          Escolha um PIN (pelo menos {l.pinDigits} dígitos) e crie uma <Ext href="https://passkeys.dev/docs/intro/what-are-passkeys/">chave de acesso</Ext> (impressão digital, rosto ou o PIN do seu dispositivo). Juntos, eles protegem a chave neste dispositivo
          e a cópia que o Vrelis guarda para a mudança para um novo dispositivo. Se seu navegador não conseguir usar uma chave de acesso dessa forma, a chave
          fica sem proteção nesse navegador e não há cópia no servidor; a frase passa a ser o único caminho para outro dispositivo.
        </li>
      </ol>

      <h2>Sua frase de recuperação</h2>
      <ul>
        <li>Guarde-a no papel, em um lugar seguro. Não a fotografe e não a salve em um computador nem em um gerenciador de senhas.</li>
        <li>
          Quem tem as 12 palavras <b>é</b> você: pode assinar como você e abrir suas cápsulas privadas. Se elas vazarem, use <b>Redefinir chave</b> em
          Conta, Chaves: você digita a frase, entra novamente, e a chave é revogada para sempre; ninguém mais pode enviar com ela nem para ela. Você informa
          desde quando ela pode ter sido roubada, e as cápsulas enviadas com ela a partir de então são marcadas como "chave revogada". Depois você cria uma
          nova chave com uma nova frase.
        </li>
        <li>
          Suas chaves anteriores podem ir para um <b>chaveiro</b>, criptografado com sua chave atual, para que as cápsulas enviadas a uma chave antiga
          continuem abrindo sem a frase antiga. Você também pode guardar a frase antiga e adicioná-la ao chaveiro depois.
        </li>
        <li>
          Se você perder um dispositivo: entre em outro e use sua chave de acesso e seu PIN (se sua chave de acesso for sincronizada) ou sua frase. Se
          esquecer o PIN: use a frase. Depois de {l.pinAttempts} PINs errados, a cópia no servidor é bloqueada; a frase grava uma nova.
        </li>
        <li>
          Se você perder a frase <b>e</b> todos os dispositivos com sua chave, suas cápsulas privadas nunca poderão ser abertas, por ninguém. Um
          administrador só pode lhe dar uma nova identidade. Revogar uma chave também exige a frase, então, sem ela, um administrador a revoga por você.
        </li>
      </ul>

      <h2>Como escrever uma cápsula</h2>
      <ul>
        <li>
          <b>Todos no piloto</b> (pública) ou <b>um membro</b> (privada). Uma cápsula pública pode ser lida por todos os membros depois do horário dela; uma
          privada, só pela pessoa para quem você escreveu, e por você, se guardar uma cópia (ativado por padrão).
        </li>
        <li>
          Toda cápsula precisa de um <b>assunto</b>. Ele nunca é criptografado: quem vê a cápsula o lê na hora, e nosso servidor também.
        </li>
        <li>
          Uma <b>introdução</b> é opcional: até {l.introChars} caracteres que podem ser lidos antes do horário, ao clicar na cápsula. Em uma cápsula
          pública, todos a leem, inclusive nosso servidor. Em uma privada, só o destinatário a lê, com a chave dele; o servidor não a vê. A mensagem e os
          arquivos continuam bloqueados em ambos os casos.
        </li>
        <li>
          Quem enviou uma cápsula privada fica selado para a chave do destinatário: ele vê isso assim que abre a cápsula com a chave dele, muito antes do
          horário. Quem tem a cápsula sem essa chave vê só o assunto e o horário. Nosso servidor sabe qual conta a enviou. Ele informa isso só ao
          destinatário, criptografado para a chave dele, para que o aplicativo possa verificar se a chave que assinou a cápsula pertence a essa conta; caso
          contrário, mostra "Remetente não verificado" no lugar de um nome.
        </li>
        <li>
          <b>Discreto</b> (cápsulas privadas): as listas do destinatário mostram "Remetente discreto" no lugar do seu nome até ele clicar na cápsula, para
          que ninguém olhando por cima do ombro dele veja quem escreve para ele. O destinatário ainda pode ver que é você, antes do horário.
        </li>
        <li>
          Escolha quando ela abre: em 5 minutos, uma hora, um dia, uma semana, um ano, ou uma data até o ano 2125. O horário real de abertura é mostrado
          antes do envio; ele é exato com margem de poucos segundos.
        </li>
        <li>
          Texto: até {l.publicText} para uma cápsula pública, {l.privateText} para uma privada. Arquivos: até {l.files} arquivos e, no total, {l.fileBytes} no Grátis ou {l.planFileBytes} com Legado ou Start. Fotos são reduzidas e perdem metadados, incluindo localização.
        </li>
        <li>
          <b>Selar e enviar</b> pede seu PIN e sua chave de acesso uma vez, para assinar a cápsula. Depois disso, ninguém pode alterá-la, e ninguém pode
          lê-la antes do horário.
        </li>
        <li>
          Antes de escrever para alguém em particular, confira a chave da pessoa: Escrever mostra a impressão digital dela (16 caracteres). Se a chave dela
          mudou desde a última vez que este navegador a viu, Escrever avisa você e espera até você confirmar com a pessoa, fora do Vrelis, que a nova chave
          é realmente dela.
        </li>
      </ul>

      <h2>Como ler</h2>
      <ul>
        <li>
          <b>Públicas</b>: cápsulas públicas. <b>Caixa de entrada</b>: cápsulas privadas para você. <b>Enviadas</b>: as suas. <b>Acompanhando</b>:
          cápsulas públicas que você está esperando. Siga um autor para ver só as cápsulas dele.
        </li>
        <li>
          <b>Ocultar</b> um membro: as cápsulas públicas dele saem de Públicas, em todos os seus dispositivos; nada muda para ele. <b>Bloquear</b> um
          membro: ele não pode mais colocar cápsulas privadas na sua Caixa de entrada (Escrever o avisa antes do envio); você ainda pode segui-lo e ler as
          cápsulas públicas dele. As cápsulas anteriores dele passam para Caixa de entrada, Bloqueados, e continuam abrindo. As duas opções ficam no menu de
          uma pessoa e na página Pessoas, junto com quem segue você. O Vrelis só responde pelo que recebe e entrega: ninguém pode impedir que alguém
          criptografe para sua chave pública fora do Vrelis.
        </li>
        <li>
          Uma cápsula privada não mostra nada até você abri-la: <b>Abrir</b> pede seu PIN, depois mostra quem a enviou e quando ela abre, e o texto quando
          chegar a hora.
        </li>
        <li>
          Sua chave fica bloqueada; cada ação que precisa dela pede o PIN. Para ler várias cápsulas em seguida, use <b>Desbloquear por … min</b> na barra
          do topo. <b>Bloquear</b> oculta tudo o que é privado de uma vez; sair da página também.
        </li>
        <li>
          Em <a href="/account">Conta</a>, Privacidade: "Mostrar remetentes nas minhas listas só depois que eu clicar em uma cápsula" trata toda cápsula
          privada como discreta, em todos os dispositivos.
        </li>
        <li>
          As listas mostram 50 cápsulas por vez; <b>Mostrar mais</b> adiciona as próximas. O Vrelis as filtra e ordena (pelo horário de abertura ou por
          quando as recebeu), então um filtro ou um grupo envia ao Vrelis os ids dos membros que você escolheu, nunca o grupo ou o nome dele. Filtrar sua
          Caixa de entrada por remetente exige sua chave, deixa de fora as cápsulas discretas e fica desativado enquanto os remetentes aparecem só depois de
          um clique.
        </li>
        <li>
          A <b>Busca</b> considera assuntos, a introdução de cápsulas públicas, quem escreveu uma cápsula pública e para quem você enviou. Ela nunca olha o
          texto aberto (só seu dispositivo o tem) nem quem enviou uma cápsula privada para você.
        </li>
      </ul>

      <h2>Envelope de resposta, CC e Correspondência</h2>
      <ul>
        <li>
          Uma cápsula privada pode levar um <b>envelope de resposta</b>: você pede a cada destinatário uma resposta privada de volta para você, selada
          para sua chave. Eles podem responder mesmo que não sigam você. Você escolhe se toda resposta abre em um horário que você define (depois desse
          horário nenhuma resposta é aceita) ou em um horário que o destinatário escolhe. Você paga antecipadamente pela cápsula e pela resposta: 2 selos por destinatário em vez de 1. A primeira resposta de um destinatário não custa nada a ele; uma segunda custa um selo a ele, e você vê as duas.
        </li>
        <li>
          <b>CC</b>: uma cápsula privada para um membro pode ir para mais membros de uma vez, e todos na lista veem quem mais a recebeu. A lista fica
          selada dentro da cápsula com a assinatura do remetente; nosso servidor nunca a vê. Um envio em grupo sem CC continua às cegas: ninguém vê quem
          mais a recebeu.
        </li>
        <li>
          Cápsulas com envelope de resposta e as respostas a elas ficam na página <b>Correspondência</b>, não em Caixa de entrada nem em Enviadas: abaixo
          de cada pedido você vê as respostas de cada destinatário em ordem, bloqueadas até o horário delas, e quem pagou por cada uma. A resposta vai só
          para o remetente.
        </li>
      </ul>

      <h2>Como guardar cápsulas sem o Vrelis</h2>
      <ul>
        <li>
          <b>Baixar</b> em uma cápsula a salva como arquivo; com anexos, é um .zip com os arquivos e o abridor offline. Selecione várias e use{" "}
          <b>Baixar selecionadas</b> para um único arquivo compactado ("Selecionar todas as exibidas" pega as cápsulas mostradas).
        </li>
        <li>
          O abridor offline é um arquivo HTML (<a href="/account">Conta</a>, ou dentro do .zip). Ele abre suas cápsulas no seu próprio computador, as
          privadas com sua frase. Ele nunca se conecta a vrelis.com; só pede ao serviço público de tempo do drand o sinal de abertura, se o arquivo ainda
          não o tiver.
        </li>
        <li>
          "Incluir texto e arquivos abertos" coloca o texto e os arquivos abertos no download sem criptografia: qualquer pessoa com o arquivo pode lê-los.
        </li>
      </ul>

      <h2>Este dispositivo</h2>
      <ul>
        <li>
          <b>Bloquear</b>: remove a chave da memória. <b>Sair</b>: encerra esta sessão; a chave fica neste dispositivo. <b>Sair de todos os dispositivos</b>:
          encerra suas sessões em todos os dispositivos. <b>Esquecer este dispositivo</b>: apaga sua chave e tudo o que o Vrelis guardou neste navegador.
        </li>
        <li>Um login dura {l.sessionDays} dias.</li>
      </ul>

      <h2>O que o Vrelis pode e não pode ver</h2>
      <ul>
        <li>
          <b>O Vrelis não pode ler</b> uma cápsula antes do horário dela, nem uma cápsula privada em momento algum (ele não tem a chave dela), nem sua
          chave, seu PIN ou sua frase.
        </li>
        <li>
          <b>O Vrelis vê</b>: seu nome e e-mail (o proprietário e os administradores veem o e-mail), quem envia uma cápsula e para quem, quando ela foi
          enviada, quando uma cápsula pública abre, os tamanhos e se uma cápsula tem arquivos. Depois do horário, uma cápsula pública pode ser lida por todos
          os membros e, portanto, também pelo Vrelis.
        </li>
        <li>
          O chaveiro e as cópias da sua chave são armazenados só criptografados; o Vrelis não consegue abri-los.
        </li>
        <li>
          Uma assinatura prova qual chave escreveu uma cápsula. Uma cápsula marcada como "chave revogada" foi assinada por uma chave que o dono depois
          informou como roubada. Que a chave pertence a uma pessoa depende da lista de membros e do nome que o remetente colocou na cápsula. Confira as
          impressões digitais com as pessoas para quem você escreve em particular.
        </li>
      </ul>

      <h2>Planos e selos</h2>
      <ul>
        <li>
          Um <b>selo</b> paga uma entrega para um destinatário. Sua própria cópia é gratuita. O Grátis dá {l.freeStamps} selos por mês, {l.freeStorage} de
          armazenamento e {l.fileBytes} de arquivos por cápsula; o Legado ({l.legacyStamps} selos por mês, {l.planStorage}, {l.planFileBytes}) foi
          concedido aos primeiros membros; o Start daria {l.planStamps} selos por mês. Os selos de um mês não se acumulam e voltam no primeiro dia do mês
          seguinte (UTC). <b>Cobrança</b> mostra o que você tem, o que resta e o que está planejado.
        </li>
        <li>
          Por enquanto nada é cobrado e nada pode ser comprado: os planos são uma prévia, e um administrador ativa um pedido manualmente. O que você já
          enviou continua legível para você e para as pessoas a quem enviou, aconteça o que acontecer com seu plano ou seus selos.
        </li>
        <li>
          A cópia para você é opcional e vem ativada por padrão. Se você a desativar, só o destinatário poderá ler essa cápsula: você não poderá lê-la,
          nem mesmo depois que ela abrir.
        </li>
      </ul>

      <h2>Limites do piloto</h2>
      <ul>
        <li>
          No máximo {l.daily} entregas por dia (sua própria cópia é gratuita; uma cápsula para um grupo conta uma por membro). Envios de arquivos: {l.uploadDaily} e {l.unusedFiles} arquivos não usados a cada 24 horas; arquivos não usados em uma cápsula em até 24 horas são apagados.
        </li>
        <li>Entre com Google, uma senha ou uma chave de acesso. Sem pagamentos, sem rede pública: as cápsulas ficam dentro do piloto.</li>
        <li>
          Convidar um endereço que já tem conta envia a esse membro um pedido de conexão; se ele aceitar, vocês passam a se seguir. Sua tela fica igual
          nos dois casos, então ninguém pode usar convites para descobrir quem é membro.
        </li>
      </ul>

      <h2>Para desenvolvedores</h2>
      <p>
        Serviços podem ler e enviar cápsulas pela <a href="/developers">API do Vrelis</a>. Um serviço recebe uma conta própria por meio de um convite de
        serviço de um administrador; para todos os outros, ele é um membro comum. Ele cria a própria chave e assina cada cápsula sozinho; os tokens de API
        dele podem ler e enviar, mas não podem gerenciar chaves nem métodos de entrada. Você pode bloquear ou ocultar um serviço como qualquer outra pessoa.
      </p>

      <h2>O que pedimos aos membros do piloto</h2>
      <ol>
        <li>Envie uma cápsula pública que abra em uma hora e uma cápsula privada para outro membro que abra em um dia.</li>
        <li>Leia as cápsulas públicas depois que abrirem e tente ler uma antes: você não deve conseguir.</li>
        <li>Um de vocês: configure sua chave em um segundo dispositivo e leia sua Caixa de entrada lá.</li>
        <li>Um de vocês: oculte outro membro e confira que as cápsulas dele somem só da sua página Públicas.</li>
      </ol>

      <h2>Problemas</h2>
      <p>
        Use <b>Relatar um problema</b> no rodapé de todas as páginas ou escreva para {link}. Diga o que você fez, o que esperava e o que aconteceu; uma
        captura de tela ajuda. O serviço, seus limites e responsabilidades estão descritos nos <a href="/terms">Termos de uso</a>.
      </p>
    </>
  );
}
